import React, { useState } from 'react';
import '../styles/mfa-helper.css';

const MfaSetupHelper = ({ onClose, customerEmail }) => {
    const [activeSection, setActiveSection] = useState('overview');
    const [searchTerm, setSearchTerm] = useState('');

    const sections = {
        overview: 'Getting Started',
        qrcode: 'QR Code Setup',
        secretkey: 'Manual Setup',
        apps: 'Authenticator Apps',
        troubleshooting: 'Troubleshooting',
        security: 'Security & Recovery'
    };

    const faqData = [
        {
            id: 1,
            question: "What is MFA and why do I need it?",
            answer: "Multi-Factor Authentication (MFA) adds an extra layer of security to your account by requiring both your password and a time-based code from your authenticator app. This protects your account even if your password is compromised.",
            category: "overview"
        },
        {
            id: 2,
            question: "I can't see the QR code in my email. What should I do?",
            answer: "If the QR code isn't visible: 1) Check your spam/junk folder, 2) Try viewing the email in a different client or browser, 3) Enable image loading in your email client, 4) Use the manual setup with the secret key instead.",
            category: "qrcode"
        },
        {
            id: 3,
            question: "How do I scan the QR code?",
            answer: "Open your authenticator app, tap 'Add Account' or '+', then 'Scan QR Code'. Point your camera at the QR code in the email. The account will be added automatically with a 6-digit code that refreshes every 30 seconds.",
            category: "qrcode"
        },
        {
            id: 4,
            question: "What if I can't scan the QR code?",
            answer: "Use manual setup instead: 1) In your authenticator app, choose 'Enter Setup Key' or 'Manual Entry', 2) Enter your email and the secret key from the email, 3) Make sure the time-based option is selected.",
            category: "secretkey"
        },
        {
            id: 5,
            question: "Where do I find the secret key?",
            answer: "The secret key is included in the same email with the QR code. Look for a long string of letters and numbers (usually 16-32 characters) labeled as 'Secret Key', 'Setup Key', or 'Manual Entry Code'.",
            category: "secretkey"
        },
        {
            id: 6,
            question: "Which authenticator app should I use?",
            answer: "Popular options include: Google Authenticator (iOS/Android), Microsoft Authenticator (iOS/Android), Authy (iOS/Android/Desktop), or 1Password (if you have a subscription). All work the same way with our system.",
            category: "apps"
        },
        {
            id: 7,
            question: "The codes aren't working when I try to log in",
            answer: "Common fixes: 1) Make sure your device's time is accurate, 2) Use the current 6-digit code (they change every 30 seconds), 3) Don't use spaces when entering the code, 4) Try the next code if one expires while typing.",
            category: "troubleshooting"
        },
        {
            id: 8,
            question: "I'm getting 'Invalid code' errors",
            answer: "This usually means a time synchronization issue. On Android: Go to Google Authenticator > Settings > Time correction. On iOS: Ensure your device time is set to automatic in Settings > General > Date & Time.",
            category: "troubleshooting"
        },
        {
            id: 9,
            question: "What if I lose my phone or get a new device?",
            answer: "Contact our support team immediately. We can temporarily disable MFA so you can log in and set it up again. This is why we recommend backing up your authenticator app or using one that syncs across devices like Authy.",
            category: "security"
        },
        {
            id: 10,
            question: "Can I use the same authenticator app for multiple accounts?",
            answer: "Yes! Most authenticator apps can store multiple accounts. Each account will have its own entry with a unique 6-digit code. Make sure to label them clearly so you don't get confused.",
            category: "apps"
        },
        {
            id: 11,
            question: "How do I backup my MFA setup?",
            answer: "Options: 1) Use Authy which syncs across devices, 2) Screenshot the QR code and store it securely, 3) Write down the secret key and store it safely, 4) Some apps have built-in export features.",
            category: "security"
        },
        {
            id: 12,
            question: "The setup email expired or I deleted it",
            answer: "No problem! Log into your account, go to Security Settings, and click 'Reset MFA Setup' or 'Generate New QR Code'. A new email with fresh codes will be sent to you.",
            category: "troubleshooting"
        }
    ];

    const filteredFAQs = faqData.filter(faq =>
        (activeSection === 'overview' || faq.category === activeSection) &&
        (searchTerm === '' ||
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const renderStepByStep = () => (
        <div className="step-by-step">
            <h3>Quick Setup Guide</h3>
            <div className="steps">
                <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                        <h4>Download an Authenticator App</h4>
                        <p>Install Google Authenticator, Microsoft Authenticator, or Authy on your phone.</p>
                    </div>
                </div>
                <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                        <h4>Check Your Email</h4>
                        <p>Find the MFA setup email sent to <strong>{customerEmail}</strong>. Check spam if needed.</p>
                    </div>
                </div>
                <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                        <h4>Add Your Account</h4>
                        <p>Scan the QR code OR manually enter the secret key from the email.</p>
                    </div>
                </div>
                <div className="step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                        <h4>Verify Setup</h4>
                        <p>Enter the 6-digit code from your app to complete setup.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTroubleshootingTips = () => (
        <div className="troubleshooting-tips">
            <h3>Quick Fixes</h3>
            <div className="tip-grid">
                <div className="tip-card">
                    <h4>⏰ Time Issues</h4>
                    <p>Ensure your device time is accurate. Go to Settings and enable automatic date/time.</p>
                </div>
                <div className="tip-card">
                    <h4>📧 Email Problems</h4>
                    <p>Check spam folder, enable image loading, or request a new setup email from your account.</p>
                </div>
                <div className="tip-card">
                    <h4>📱 App Issues</h4>
                    <p>Try restarting the authenticator app or re-adding the account if codes aren't working.</p>
                </div>
                <div className="tip-card">
                    <h4>🔢 Code Problems</h4>
                    <p>Use the current code (they refresh every 30 seconds) and enter without spaces.</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="modal mfa-helper-modal">
            <div className="modal-content mfa-helper-content">
                <div className="mfa-header">
                    <h1>Intellommerce.ai MFA Setup Assistant</h1>
                    <span className="close" onClick={onClose}>&times;</span>
                </div>

                <div className="mfa-search">
                    <input
                        type="text"
                        placeholder="Search for help..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="mfa-layout">
                    <div className="mfa-sidebar">
                        <nav className="mfa-nav">
                            {Object.entries(sections).map(([key, label]) => (
                                <button
                                    key={key}
                                    className={`nav-button ${activeSection === key ? 'active' : ''}`}
                                    onClick={() => setActiveSection(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </nav>

                        <div className="contact-support">
                            <h4>Still Need Help?</h4>
                            <p>Contact our support team:</p>
                            <button className="support-btn" onClick={() => window.open('https://wa.me/32476371207?text=Hello%20there!%20I%20need%20help%20with%20MFA.', '_blank')}>Live Chat (WhatsApp)</button>
                            <button className="support-btn" onClick={() => window.open('mailto:support@intellommerce.ai', '_blank')}>Email Support</button>
                        </div>
                    </div>

                    <div className="mfa-main-content">
                        {activeSection === 'overview' && (
                            <div className="overview-section">
                                <div className="welcome-message">
                                    <h2>Welcome to MFA Setup!</h2>
                                    <p>We've sent you an email with everything you need to secure your account. This guide will walk you through the entire process.</p>
                                    {customerEmail && (
                                        <div className="email-reminder">
                                            <strong>Check your email:</strong> {customerEmail}
                                        </div>
                                    )}
                                </div>
                                {renderStepByStep()}
                                {renderTroubleshootingTips()}
                            </div>
                        )}

                        {activeSection !== 'overview' && (
                            <div className="faq-section">
                                <h2>{sections[activeSection]}</h2>
                                <div className="faq-list">
                                    {filteredFAQs.map(faq => (
                                        <div key={faq.id} className="faq-item">
                                            <h3 className="faq-question">{faq.question}</h3>
                                            <p className="faq-answer">{faq.answer}</p>
                                        </div>
                                    ))}
                                    {filteredFAQs.length === 0 && (
                                        <p className="no-results">No matching questions found. Try a different search term or browse other sections.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mfa-footer">
                    <div className="security-notice">
                        <strong>🔒 Security Note:</strong> Never share your secret key or QR code with anyone. Our support team will never ask for your authentication codes.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MfaSetupHelper;